import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowUp, Loader2, Plus, Search } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";

type FeatureRequest = {
  id: string;
  title: string;
  description: string | null;
  upvote_count: number;
  created_at: string;
  created_by?: string;
};

type FeatureRequestComment = {
  id: string;
  feature_request_id: string;
  content: string;
  created_at: string;
};

export default function FeatureRequests() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<FeatureRequest[]>([]);
  const [userVotes, setUserVotes] = useState<Record<string, boolean>>({});
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [query, setQuery] = useState("");
  const searchTerm = query.trim();
  const [activeComments, setActiveComments] = useState<Record<string, FeatureRequestComment[]>>({});
  const [newComment, setNewComment] = useState<Record<string, string>>({});

  const loadData = async () => {
    setIsLoading(true);
    try {
      let request = supabase
        .from("feature_requests")
        .select("id, title, description, upvote_count, created_at, created_by")
        .order("upvote_count", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(50);

      if (searchTerm.length > 0) {
        // Search title or description
        request = request.or(
          `title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`
        );
      }

      const { data, error } = await request;
      if (error) throw error;
      setItems(data || []);

      // Load user votes for the rendered items
      if (user?.id && (data || []).length > 0) {
        const ids = (data || []).map((d) => d.id);
        const { data: votes, error: votesError } = await supabase
          .from("feature_request_votes")
          .select("feature_request_id")
          .in("feature_request_id", ids)
          .eq("user_id", user.id);
        if (votesError) throw votesError;
        const map: Record<string, boolean> = {};
        (votes || []).forEach((v: any) => (map[v.feature_request_id] = true));
        setUserVotes(map);
      } else {
        setUserVotes({});
      }

      // Preload comments for visible items (shallow - last 3)
      const ids = (data || []).map((d) => d.id);
      if (ids.length > 0) {
        const { data: comments } = await supabase
          .from("feature_request_comments")
          .select("id, feature_request_id, content, created_at")
          .in("feature_request_id", ids)
          .order("created_at", { ascending: false });
        const grouped: Record<string, FeatureRequestComment[]> = {};
        (comments || []).forEach((c) => {
          if (!grouped[c.feature_request_id]) grouped[c.feature_request_id] = [];
          if (grouped[c.feature_request_id].length < 3) grouped[c.feature_request_id].push(c);
        });
        setActiveComments(grouped);
      } else {
        setActiveComments({});
      }
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Load failed",
        description: e.message || "Unable to load feature requests",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadData();
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, user?.id]);

  const handleUpvote = async (id: string) => {
    if (!user?.id) {
      toast({
        variant: "destructive",
        title: "Login required",
        description: "You must be logged in to vote",
      });
      return;
    }
    try {
      // Try to insert vote; if unique violation, remove the vote (toggle)
      const { error: insertError } = await supabase
        .from("feature_request_votes")
        .insert({ feature_request_id: id, user_id: user.id });
      if (insertError) {
        // Unique violation => toggle off
        if ((insertError as any).code === "23505") {
          await supabase
            .from("feature_request_votes")
            .delete()
            .eq("feature_request_id", id)
            .eq("user_id", user.id);
        } else {
          throw insertError;
        }
      }
      await loadData();
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Vote failed",
        description: e.message || "Unable to update vote",
      });
    }
  };

  const handleCreate = async () => {
    if (!user?.id) {
      toast({
        variant: "destructive",
        title: "Login required",
        description: "You must be logged in to submit a request",
      });
      return;
    }
    if (!newTitle.trim()) {
      toast({
        variant: "destructive",
        title: "Missing title",
        description: "Please provide a title",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("feature_requests").insert({
        title: newTitle.trim(),
        description: newDescription.trim() || null,
        created_by: user.id,
      });
      if (error) throw error;
      setNewTitle("");
      setNewDescription("");
      toast({ title: "Submitted", description: "Feature request added" });
      await loadData();
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Submit failed",
        description: e.message || "Unable to submit request",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout title="Feature Requests">
      <div className="container mx-auto px-4 py-4 max-w-4xl">
      <h1 className="text-2xl font-bold mb-4 hidden">Feature Requests</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Start typing your feature below – we’ll search existing requests as you type. If you find a similar request, upvoting it helps us prioritize it over duplicates.
      </p>

      {/* Search */}
      <div className="flex gap-2 items-center mb-6">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search feature requests"
            className="pl-9"
          />
        </div>
        <Button onClick={loadData} variant="outline">
          Refresh
        </Button>
      </div>

      <div className="grid md:grid-cols-5 gap-6">
        <div className="md:col-span-3 space-y-3">
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading...
            </div>
          ) : items.length === 0 ? (
            <div className="text-sm text-muted-foreground">No matching requests.</div>
          ) : (
            items.map((item) => {
              const voted = !!userVotes[item.id];
              return (
                <div key={item.id} className="flex flex-col gap-3 p-4 border rounded-lg bg-background">
                  <div className="flex items-start gap-3">
                    <Button
                      variant={voted ? "default" : "outline"}
                      size="sm"
                      className="flex flex-col items-center justify-center w-12 h-12"
                      onClick={() => handleUpvote(item.id)}
                    >
                      <ArrowUp className="h-4 w-4" />
                      <span className="text-xs">{item.upvote_count}</span>
                    </Button>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-semibold">{item.title}</div>
                        {user?.id && item.created_by === user.id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={async () => {
                              const { error } = await supabase
                                .from("feature_requests")
                                .delete()
                                .eq("id", item.id);
                              if (error) {
                                toast({ variant: "destructive", title: "Delete failed", description: error.message });
                              } else {
                                toast({ title: "Deleted", description: "Feature request removed" });
                                await loadData();
                              }
                            }}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                      {item.description && (
                        <div className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                          {item.description}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Comments preview */}
                  {activeComments[item.id]?.length ? (
                    <div className="mt-1 space-y-2">
                      {activeComments[item.id].map((c) => (
                        <div key={c.id} className="text-xs text-muted-foreground border-l pl-2 flex items-start justify-between gap-2">
                          <span>{c.content}</span>
                          {user?.id && (
                            <Button
                              size="xs" asChild={false}
                              variant="ghost"
                              className="h-6 px-2 text-muted-foreground hover:text-destructive"
                              onClick={async () => {
                                // Backend policy allows delete only if created_by = auth.uid()
                                const { error } = await supabase
                                  .from("feature_request_comments")
                                  .delete()
                                  .eq("id", c.id);
                                if (error) {
                                  toast({ variant: "destructive", title: "Delete failed", description: error.message });
                                } else {
                                  await loadData();
                                }
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {/* Add comment */}
                  <div className="flex gap-2">
                    <Input
                      value={newComment[item.id] || ""}
                      onChange={(e) => setNewComment((prev) => ({ ...prev, [item.id]: e.target.value }))}
                      placeholder="Add a comment"
                    />
                    <Button
                      variant="outline"
                      disabled={
                        !user?.id || !((newComment[item.id] || "").trim())
                      }
                      onClick={async () => {
                        const content = (newComment[item.id] || "").trim();
                        if (!content) return;
                        const { error } = await supabase
                          .from("feature_request_comments")
                          .insert({ feature_request_id: item.id, created_by: user.id, content });
                        if (error) {
                          toast({ variant: "destructive", title: "Failed", description: error.message });
                          return;
                        }
                        setNewComment((prev) => ({ ...prev, [item.id]: "" }));
                        await loadData();
                      }}
                    >
                      Comment
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="md:col-span-2">
          <div className="p-4 border rounded-lg bg-background">
            <div className="flex items-center gap-2 mb-2">
              <Plus className="h-4 w-4" />
              <div className="font-semibold">Add a Feature Request</div>
            </div>
            <div className="space-y-3">
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Short title"
              />
              <Textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Describe the feature and why it matters"
                className="min-h-[120px]"
              />
              <Button onClick={handleCreate} disabled={isSubmitting} className="w-full">
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...
                  </>
                ) : (
                  "Submit Feature Request"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </DashboardLayout>
  );
}


